import { getHistoryTooltipMessage } from '@/layouts/campaigns/blocks/getHistoryTooltipMessage'
import { Tooltip, TooltipAlertCircle } from '@/ui'
import { AttrType, Node, PeriodType, PointValue, SetAttributeValuesNode, TimerNode } from '@/api/dashboard'
import { DATE_TIME_NOW_ATTRIBUTE_VALUE, EventActionNodeType } from '@/layouts/campaigns/types'
import { useContext } from 'react'
import { CampaignContext, ICampaignContext } from '@/expr/Context'
import { getNodeColor } from '@/layouts/campaigns/icons'
import { useTranslation } from 'react-i18next'
import { DescConditionNode } from '@/layouts/campaigns/blocks/descriptions/DescConditionNode'
import { CouponDesc } from '@/layouts/campaigns/blocks/descriptions/CouponDesc'
import { DescVirtualSKUActionNode } from '@/layouts/campaigns/blocks/descriptions/DescVirtualSKUActionNode'
import { useSegmentsQuery } from '@/layouts/segment/api/useSegmentsQuery'
import { useGenerateCompanyPath } from '@/libs'
import { getTime } from '@/layouts/campaigns/editors/TimerEditor'
import { useStoresQuery } from '@/layouts/store/api'
import { DescCommunicationNode } from '@/layouts/campaigns/blocks/descriptions/DescCommunicationNode'
import { capitalizeFirstLetter } from '@/util'
import { useGameSettingsQuery } from '@/api/useGameSettingsQuery'
import { CreateItemBannerActionNodeDesc } from '@/layouts/campaigns/blocks/descriptions/CreateItemBannerActionNodeDesc'
import { NodeSingleValue } from '@/layouts/campaigns/components/NodeSingleValue'

interface DescProps {
  node: Node
  width?: string
  selected?: boolean
  visitsData?: PointValue
  purchaseData?: PointValue
}

export const DescBlock = ({ node, width, selected, visitsData, purchaseData }: DescProps) => {
  const { companyId, gameId } = useGenerateCompanyPath()
  const { t } = useTranslation()
  const { data: gameSettings } = useGameSettingsQuery(companyId, gameId)
  const context = useContext(CampaignContext) as ICampaignContext
  const { data: stores = [] } = useStoresQuery(companyId, gameId, {
    limit: 100,
  })
  const { data: segments = [] } = useSegmentsQuery(companyId, gameId, {
    limit: 100,
  })

  const getTimerDesc = (node: TimerNode) => {
    let time = getTime(node.delay)
    let subTitle = ''
    if (time[PeriodType.Day]) {
      subTitle = `${time[PeriodType.Day]}${t('time.interval.days')} `
    }
    if (time[PeriodType.Hour]) {
      subTitle += `${time[PeriodType.Hour]}${t('time.interval.hours')} `
    }
    if (time[PeriodType.Minute]) {
      subTitle += `${time[PeriodType.Minute]}${t('time.interval.minutes')} `
    }
    if (time[PeriodType.Second]) {
      subTitle += `${time[PeriodType.Second]}${t('time.interval.seconds')}`
    }

    return subTitle
  }

  const getAttrDesc = (node: SetAttributeValuesNode) => {
    const attrNames = Object.keys(node.values || {})
    if (attrNames.length) {
      const attr = gameSettings?.player_attributes?.find(it => it.name == attrNames[0])

      if (!attr) {
        return '...'
      }

      const value = (node.values as Record<string, object>)[attr.name]

      if (attr.type == AttrType.Date && (value as unknown as number) == DATE_TIME_NOW_ATTRIBUTE_VALUE) {
        return `${attr.name} -> ${t('campaign.set-attr-editor.now')}`
      }

      return `${attr.name} -> ${value}`
    }
  }

  const renderTitle = () => {
    switch (node.model_type) {
      case EventActionNodeType.CreateCouponNode:
        return t('campaign.node-type.CreateCouponNode')
      case EventActionNodeType.ItemDiscountOfferActionNode:
        return t('campaign.node-type.ItemDiscountOfferActionNode')
      case EventActionNodeType.ItemBonusOfferActionNode:
        return t('campaign.node-type.ItemBonusOfferActionNode')
      case EventActionNodeType.ItemOfferActionNode:
        return t('campaign.node-type.ItemOfferActionNode')
      case EventActionNodeType.CreateVirtualSKUNode:
        return t('campaign.node-type.CreateVirtualSKUNode')
      case EventActionNodeType.CreateItemBannerActionNode:
        return t('campaign.node-type.CreateItemBannerActionNode')
      case EventActionNodeType.AddToSegmentNode:
        return t('campaign.block.AddToSegmentNode.add-user-to-segment')
      case EventActionNodeType.EmailActionNode:
      case EventActionNodeType.MobilePushActionNode:
      case EventActionNodeType.BrowserPushNotificationActionNode:
      case EventActionNodeType.MobilePopupActionNode:
      case EventActionNodeType.BrowserPopupNotificationActionNode:
        return t(`campaign.node-type.${node.model_type}`)
      case EventActionNodeType.ConditionNode:
        return t('campaign.block.ConditionNode')
      case EventActionNodeType.SplitNode:
        let arr = node.distribution
        if (arr?.length) {
          return [node.title, arr[0].value + '/' + (arr[1].value - arr[0].value)]
        }
        return ''
      case EventActionNodeType.TimerNode:
        return t('campaign.block.wait')
      case EventActionNodeType.CreateUserStoreSettingsNode:
        return t('campaign.node-type.CreateUserStoreSettingsNode')
      case EventActionNodeType.AddStoreNode:
        return t('campaign.node-type.AddStoreNode')
      case EventActionNodeType.SortStoreItemsNode:
        return t('campaign.node-type.SortStoreItemsNode')
      case EventActionNodeType.SetAttributeValuesNode:
        return t('campaign.node-type.SetAttributeValuesNode')
    }
  }

  const renderDesc = () => {
    switch (node.model_type) {
      case EventActionNodeType.CreateCouponNode:
        return <CouponDesc node={node} />
      case EventActionNodeType.ItemDiscountOfferActionNode:
      case EventActionNodeType.ItemBonusOfferActionNode:
      case EventActionNodeType.ItemOfferActionNode:
      case EventActionNodeType.CreateVirtualSKUNode:
        return <DescVirtualSKUActionNode node={node} />
      case EventActionNodeType.CreateItemBannerActionNode:
        return <CreateItemBannerActionNodeDesc node={node} graph={context.graph} type={context.campaign.type} />
      case EventActionNodeType.AddToSegmentNode:
        return segments.find(it => it.slug == node.segment_slug)?.name || ''
      case EventActionNodeType.EmailActionNode:
      case EventActionNodeType.MobilePushActionNode:
      case EventActionNodeType.BrowserPushNotificationActionNode:
      case EventActionNodeType.MobilePopupActionNode:
      case EventActionNodeType.BrowserPopupNotificationActionNode:
        return <DescCommunicationNode node={node} graph={context.graph} type={context.campaign.type} />
      case EventActionNodeType.ConditionNode:
        return <DescConditionNode expr={node.expr} />
      case EventActionNodeType.TimerNode:
        return getTimerDesc(node)
      case EventActionNodeType.CreateUserStoreSettingsNode:
        return stores?.find(it => it.id == node.store_id)?.name || ''
      case EventActionNodeType.AddStoreNode:
        return stores?.find(it => it.id == node.store_id)?.name || ''
      case EventActionNodeType.SortStoreItemsNode:
        return node.sort_by && node.order
          ? `${t('store.SortStoreItemsNode.sort_by')} '${capitalizeFirstLetter(node.sort_by)}' (${node.order})`
          : ''
      case EventActionNodeType.SetAttributeValuesNode:
        return getAttrDesc(node)
    }
    return node.title
  }

  const color = getNodeColor(node.model_type)
  const content = (
    <div
      style={{
        boxShadow: selected ? `1px 4px 22px 0px ${color}` : '',
        border: `2px solid ${color}`,
        left: `calc(100% + 16px)`,
        width: width || 'auto',
      }}
      className="absolute flex max-h-[112px] min-h-[68px] min-w-[68px] max-w-[400px] flex-col items-start justify-center whitespace-nowrap rounded-md bg-fg-primary px-4 py-3"
    >
      {node.description && (
        <div className="absolute right-1 top-1">
          <TooltipAlertCircle message={node.description} />
        </div>
      )}
      {visitsData && <NodeSingleValue value={visitsData} secondValue={purchaseData} />}
      <div className="w-full truncate">{renderTitle()}</div>
      <div className="w-full truncate">{renderDesc()}</div>
    </div>
  )

  if (context.historyMode) {
    const message = getHistoryTooltipMessage(node)

    if (message) {
      return <Tooltip message={message}>{content}</Tooltip>
    }
  }

  return content
}
